import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Check, X } from "lucide-react";
import { useAuth, API } from "../App";
import { useContentBlocks, resolveBlock, dispatchContentBlockChange } from "../hooks/useContentBlocks";
import { usePreviewMode } from "../hooks/usePreviewMode";

/**
 * EditableText — public-page text that doubles as an in-place CMS editor for
 * the founder + master admin.
 *
 * Usage:
 *   <EditableText
 *     blockKey="prelaunch.hero_headline_lead"
 *     fallback="The home for"
 *     as="span"
 *     className="text-4xl font-light"
 *   />
 *
 * Rules:
 *  - For non-platform-editors (members, regular admins, anonymous): renders
 *    purely as `<as>` with the resolved value. No edit affordances.
 *  - For founder/master-admin: renders a tiny pencil ✏️ icon on hover.
 *    Clicking enters edit mode (textarea overlay). Save persists via PUT
 *    /api/admin/content-blocks/{block_key} and broadcasts the new value
 *    to every other EditableText on the page so the update is instant.
 *  - When `usePreviewMode` is enabled, the pencil is hidden — the editor
 *    sees the page exactly as an anonymous visitor would.
 *
 * Multiline default: pass `multiline={true}` for paragraphs; textarea grows.
 */
const EditableText = ({
  blockKey,
  fallback,
  as: Tag = "span",
  className = "",
  style,
  multiline = false,
  children,
  ...rest
}) => {
  const { user } = useAuth();
  const { map } = useContentBlocks();
  const previewMode = usePreviewMode();
  const value = resolveBlock(map, blockKey, fallback ?? (typeof children === "string" ? children : ""));

  const canEdit = (() => {
    if (previewMode) return false;
    if (!user) return false;
    const email = (user.email || "").toLowerCase();
    // Founder-only inline editing (per spec — invisible to all admins).
    // Master admins keep access to the /admin/content backup panel.
    return email === "ramneetrdh@gmail.com" || user.admin_role === "founder";
  })();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const save = async () => {
    if (saving) return;
    const trimmed = (draft || "").trim();
    if (!trimmed) {
      toast.error("Value cannot be empty. Use the CMS panel to reset to default if you want to revert.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content-blocks/${encodeURIComponent(blockKey)}`, { value: trimmed });
      dispatchContentBlockChange(blockKey, trimmed);
      setEditing(false);
      toast.success("Saved — live on the page");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  // Plain render path for everyone except editors (or when previewing as visitor)
  if (!canEdit) {
    return <Tag className={className} style={style} {...rest}>{value}</Tag>;
  }

  if (editing) {
    return (
      <span className="relative inline-block w-full" data-testid={`editable-${blockKey}-edit`}>
        {multiline ? (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(2, Math.min(8, draft.split("\n").length + 1))}
            maxLength={5000}
            className={`w-full bg-white border-2 border-[#6B4E8A] rounded-lg p-2 outline-none ${className}`}
            style={style}
            data-testid={`editable-textarea-${blockKey}`}
          />
        ) : (
          <input
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={500}
            className={`w-full bg-white border-2 border-[#6B4E8A] rounded-lg p-2 outline-none ${className}`}
            style={style}
            data-testid={`editable-input-${blockKey}`}
          />
        )}
        <div className="flex items-center gap-1 mt-1">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#10B981] text-white text-xs hover:bg-[#059669] disabled:opacity-50"
            data-testid={`editable-save-${blockKey}`}
          >
            <Check className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-[#E5DFEC] text-[#64748B] text-xs hover:bg-[#FAF7F2]"
            data-testid={`editable-cancel-${blockKey}`}
          >
            <X className="w-3 h-3" /> Cancel
          </button>
          <span className="text-[10px] text-[#94A3B8] ml-1">{draft.length}/{multiline ? 5000 : 500}</span>
        </div>
      </span>
    );
  }

  return (
    <Tag className={`relative group ${className}`} style={style} {...rest}>
      {value}
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDraft(value);
          setEditing(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setDraft(value);
            setEditing(true);
          }
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#6B4E8A] text-white align-middle cursor-pointer"
        title="Edit this text (founder only)"
        data-testid={`editable-pencil-${blockKey}`}
        aria-label={`Edit ${blockKey}`}
      >
        <Pencil className="w-3 h-3" />
      </span>
    </Tag>
  );
};

export default EditableText;
